import React from "react"
import styled from "styled-components"
import { rem } from "polished"
import { media } from "@sr/common"
import SanityImage from "gatsby-plugin-sanity-image"

import {
  SemiBdTitle5,
  SanityImg,
  SemiBdTitle3,
  Accordion,
  SemiBdTitle4,
  RegBody,
  SemiBdTitle6,
  IconChevronDown,
} from "@sr/gatsby-theme-ui"

import { Link } from "gatsby"

export type ProductsHubSolutionTab = {
  label: string
  title: string
  text: string
  solutionsTitle: string
  img: SanityImg
  recommendedSolutions?: {
    recommendedSolution: {
      title: string
      path: { current: string }
    }
  }[]
}
export interface ProductsHubSolutionTabsProps {
  tabs: {
    title: string
    productHubSolutionsTabItems: ProductsHubSolutionTab[]
  }
}

export const handleAnchorClick = () => {
  const anchorTarget = document.getElementById("solutions-list")
  anchorTarget?.scrollIntoView({ behavior: "smooth", block: "start" })
}

const ProductsHubSolutionTabsMobile = ({
  tabs,
}: ProductsHubSolutionTabsProps) => {
  const accordionTabs = tabs?.productHubSolutionsTabItems.map(
    (tab: ProductsHubSolutionTab, i: number) => {
      return {
        title: <SemiBdTitle5>{tab.label}</SemiBdTitle5>,
        id: i,
        content: (
          <div className="products-hub-solutions-tabs-mobile__accordion-item-container">
            <SemiBdTitle4>{tab.title}</SemiBdTitle4>
            <RegBody>{tab.text}</RegBody>
            {tab?.img?.asset && (
              <SanityImage
                alt={tab?.img?.alt || "tab image"}
                asset={tab?.img?.asset}
                className="product-hub-solutions-tabs-mobile__tab-image"
                crop={tab?.img?.crop}
                hotspot={tab?.img?.hotspot}
              />
            )}
            <SemiBdTitle5 className="products-hub-solutions-tabs-mobile__accordion-item-solutions-title">
              {tab.solutionsTitle}
            </SemiBdTitle5>
            {tab?.recommendedSolutions && (
              <div className="products-hub-solutions-tabs-mobile__accordion-item-recommended-container">
                {tab.recommendedSolutions.map((solution, i: number) => {
                  return (
                    <Link
                      to={solution.recommendedSolution.path.current}
                      key={i}
                    >
                      <SemiBdTitle6>
                        <li>{solution.recommendedSolution.title}</li>
                      </SemiBdTitle6>
                    </Link>
                  )
                })}
              </div>
            )}
          </div>
        ),
      }
    }
  )

  return (
    <ProductsHubSolutionTabsMobileStyled>
      <SemiBdTitle3>{tabs?.title}</SemiBdTitle3>
      <Accordion
        className="products-hub-solutions-tabs-mobile__accordion"
        collapsible={true}
        defaultActiveItem={0}
        items={accordionTabs}
      />
      <div className="products-hub-solutions-tabs-mobile__link-container">
        <a
          onClick={() => handleAnchorClick()}
          className="products-hub-solutions-tabs-mobile__link"
        >
          <div className="products-hub-solutions-tabs-mobile__link-icon-container">
            <IconChevronDown color="var(--color-primary)" />
          </div>
          <SemiBdTitle4 className="products-hub-solutions-tabs-mobile__link-text">
            View all solutions
          </SemiBdTitle4>
        </a>
      </div>
    </ProductsHubSolutionTabsMobileStyled>
  )
}

export default ProductsHubSolutionTabsMobile

const ProductsHubSolutionTabsMobileStyled = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${rem(40)} ${rem(16)} 0;

  @media ${media.midUp} {
    padding: ${rem(72)} ${rem(64)} 0;
  }

  h3 {
    font-size: ${rem(32)};
    line-height: ${rem(40)};

    @media ${media.midUp} {
      font-size: ${rem(40)};
      line-height: ${rem(48)};
      max-width: ${rem(616)};
    }
  }

  .products-hub-solutions-tabs-mobile__accordion {
    padding-top: ${rem(16)};

    @media ${media.midUp} {
      padding-top: ${rem(40)};
    }

    h4 {
      font-size: ${rem(24)};
      line-height: ${rem(32)};
      max-width: ${rem(265)};

      @media ${media.midUp} {
        font-size: ${rem(32)};
        line-height: ${rem(40)};
        max-width: ${rem(600)};
        padding-top: ${rem(16)};
      }
    }

    p {
      padding-top: ${rem(8)};

      @media ${media.midUp} {
        padding-top: ${rem(16)};
      }
    }

    img {
      margin-top: ${rem(16)};
      border-radius: ${rem(8)};

      @media ${media.midUp} {
        margin-top: ${rem(32)};
      }
    }

    .products-hub-solutions-tabs-mobile__accordion-item-solutions-title {
      font-size: ${rem(20)};
      line-height: ${rem(32)};
      padding-top: ${rem(16)};

      @media ${media.midUp} {
        font-size: ${rem(24)};
        line-height: ${rem(32)};
        padding-top: ${rem(40)};
      }
    }

    .products-hub-solutions-tabs-mobile__accordion-item-recommended-container {
      padding: ${rem(16)} 0 ${rem(32)} ${rem(16)};
      display: flex;
      flex-direction: column;
      gap: ${rem(8)};

      @media ${media.midUp} {
        max-height: ${rem(208)};
        max-width: 100%;
        flex-wrap: wrap;
      }

      h6 {
        font-size: ${rem(20)};
        line-height: ${rem(24)};

        @media ${media.midUp} {
          font-size: ${rem(24)};
          line-height: ${rem(32)};
        }
      }
    }

    //manual overrides for accordion component
    .accordion-header__button {
      padding-left: 0;
    }

    .accordion-header {
      padding: ${rem(8)} 0;
    }

    h5 {
      font-size: ${rem(20)};
      line-height: ${rem(32)};

      @media ${media.midUp} {
        font-size: ${rem(24)};
        line-height: ${rem(32)};
      }
    }

    .active {
      h5 {
        color: var(--color-primary);
      }
      border-bottom: none;
    }

    .inactive {
      h5 {
        color: var(--color-gray400);
      }
    }

    .accordion-content__container {
      padding: 0;
    }
  }

  .products-hub-solutions-tabs-mobile__link-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: ${rem(40)};

    @media ${media.midUp} {
      padding-top: ${rem(56)};
    }

    .products-hub-solutions-tabs-mobile__link {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: ${rem(8)};
      color: var(--color-primary);
      text-decoration: none;
    }

    .products-hub-solutions-tabs-mobile__link-icon-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${rem(32)};
      height: ${rem(32)};
      border: ${rem(1)} solid var(--color-primary);
      border-radius: ${rem(32)};
    }
  }
`
